/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChartBarIcon,
  BeakerIcon,
  ClipboardDocumentCheckIcon,
  ArrowTrendingUpIcon,
  PlusIcon,
  FolderIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useTest } from '../context/TestContext';
import { useError } from '../context/ErrorContext';
import Sidebar from '../components/Sidebar';
import { dataService } from '../services/dataService';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import HamburgerButton from '../components/HamburgerButton';
import TestTake from '../components/TestTake';
import { apiService } from '../services/apiService';

const Test = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { createdTests, loadTests } = useTest();
  const { showError } = useError();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isCollapsed = localStorage.getItem('sidebar_collapsed') === 'true';
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTest, setCurrentTest] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await loadTests();
        const results = await dataService.getTestResults();
        setTestResults(results);
      } catch (error) {
        console.error('Error loading test data:', error);
        showError('Failed to load test data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [loadTests, showError]);

  const calculateAverageScore = () => {
    if (testResults.length === 0) return 0;
    const totalScore = testResults.reduce((sum, result) => sum + result.score, 0);
    return Math.round(totalScore / testResults.length);
  };

  const getRecentPerformance = () => {
    return testResults
      .slice(0, 3)
      .map(result => ({
        score: result.score,
        title: result.testId?.title || 'Untitled Test'
      }));
  };

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTestSubmit = async (answers) => {
    try {
      const result = await apiService.post('/api/test-results', {
        answers: answers,
        testId: currentTest._id
      });
      navigate(`/test/results/${result._id}`);
    } catch (error) {
      console.error('Error submitting test:', error);
      showError('Failed to submit test');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      <Sidebar 
        isSidebarOpen={isSidebarOpen} 
        toggleSidebar={toggleSidebar}
      />
      <HamburgerButton 
        onClick={toggleSidebar}
        isOpen={isSidebarOpen}
      />

      <main className={`transition-all duration-500 ease-in-out ${
        'ml-0 lg:ml-64'
      }`}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4 sm:py-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">Test Center</h1>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Overall Progress */}
            <div className="bg-dark-card rounded-xl p-5 hover:bg-dark-hover/50 transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-white">Overall Progress</h2>
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <ChartBarIcon className="w-5 h-5 text-blue-500" />
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-32 h-32 mb-4">
                  <CircularProgressbar
                    value={calculateAverageScore()}
                    text={`${calculateAverageScore()}%`}
                    styles={buildStyles({
                      pathColor: '#3b82f6',
                      textColor: '#ffffff',
                      trailColor: '#1f2937',
                      backgroundColor: '#1f2937',
                      textSize: '20px',
                      pathTransitionDuration: 0.5,
                      pathTransition: 'ease-out',
                      strokeLinecap: 'round',
                    })}
                  />
                </div>
                <div className="text-sm text-gray-400 text-center">
                  Average Score
                </div>
              </div>
            </div>

            {/* Recent Performance */}
            <div className="bg-dark-card rounded-xl p-5 hover:bg-dark-hover/50 transition-all duration-200">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-medium text-white">Recent Tests</h2>
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <ArrowTrendingUpIcon className="w-5 h-5 text-green-500" />
                </div>
              </div>
              <div className="space-y-2">
                {getRecentPerformance().map((result, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between text-sm bg-dark-bg rounded-lg p-2.5 hover:bg-dark-hover/50 transition-all duration-200"
                  >
                    <span className="text-gray-400 truncate flex-1 mr-4 font-medium">{result.title}</span>
                    <span className={`px-2.5 py-1 rounded-md font-medium ${
                      result.score >= 70 ? 'bg-green-500/10 text-green-500' :
                      result.score >= 40 ? 'bg-yellow-500/10 text-yellow-500' :
                      'bg-red-500/10 text-red-500'
                    }`}>
                      {result.score}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Test Activity */}
            <div className="bg-dark-card rounded-xl p-5 hover:bg-dark-hover/50 transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-white">Test Activity</h2>
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <BeakerIcon className="w-5 h-5 text-purple-500" />
                </div>
              </div>
              <div className="flex flex-col items-center justify-center">
                <div className="text-5xl font-bold text-white mb-2">
                  {testResults.length}
                </div>
                <div className="text-sm text-gray-400">Tests Completed</div>
                <div className="mt-4 w-full bg-dark-bg rounded-full h-2">
                  <div 
                    className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((testResults.length / 10) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-dark-card rounded-xl p-5 hover:bg-dark-hover/50 transition-all duration-200">
              <h2 className="text-lg font-medium text-white mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/test/create')}
                  className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  <span>Create New Test</span>
                </button>
                <button
                  onClick={() => navigate('/test/created')}
                  className="w-full px-4 py-3 bg-dark-bg text-gray-200 rounded-lg hover:bg-dark-hover transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <FolderIcon className="w-5 h-5" />
                  <span>Created Tests</span>
                </button>
              </div>
            </div>
          </div>

          {/* Recent Test Results */}
          <div className="bg-dark-card rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-medium text-white">Recent Test Results</h2>
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <ClipboardDocumentCheckIcon className="w-5 h-5 text-blue-500" />
              </div>
            </div>

            {testResults.length === 0 ? (
              <div className="text-center py-12">
                <div className="bg-dark-bg rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <BeakerIcon className="w-8 h-8 text-gray-500" />
                </div>
                <p className="text-gray-400 mb-4">No tests completed yet</p>
                <button
                  onClick={() => navigate('/test/create')}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Create Your First Test
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {testResults.map((result, index) => (
                  <div
                    key={index}
                    className="bg-dark-bg rounded-xl p-4 hover:bg-dark-hover/50 transition-all duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-white mb-1">
                          {result.testId?.title || 'Untitled Test'}
                        </h3>
                        <div className="text-sm text-gray-400 flex items-center">
                          <ClockIcon className="w-4 h-4 mr-1" />
                          {new Date(result.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold mb-1 ${
                          result.score >= 70 ? 'text-green-500' :
                          result.score >= 40 ? 'text-yellow-500' :
                          'text-red-500'
                        }`}>
                          {result.score}%
                        </div>
                        <div className="text-sm text-gray-400">
                          {result.answers?.length || 0} questions
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Test; 